import { Rule } from '../components/QualificationRules'
import { PoolLead } from '../data/leadPool'

// Field mapping from display name to PoolLead property
const FIELD_MAP: Record<string, keyof PoolLead> = {
  'Credit Score': 'creditScoreNum',
  LTV: 'ltvPercent',
  'Lead Source': 'leadSource',
  'Loan Type': 'loanType',
  Income: 'incomeK',
  'Co-Borrower': 'coBorrower',
  'Veteran Status': 'veteranStatus',
  'Current Rate': 'currentRate',
}

export function parseNumericValue(value: string): number {
  if (!value) return 0
  // Remove $, %, k, and commas
  const clean = value.toString().replace(/[$,%kK]/g, '')
  return parseFloat(clean)
}

export function evaluateRule(lead: PoolLead, rule: Rule): boolean {
  const fieldKey = FIELD_MAP[rule.field]
  if (!fieldKey) return false

  const leadValue = lead[fieldKey]

  // Handle boolean fields (Co-Borrower, Veteran Status)
  if (typeof leadValue === 'boolean') {
    const ruleValueBool =
      rule.value.toLowerCase() === 'yes' || rule.value.toLowerCase() === 'true'
    return rule.operator === '='
      ? leadValue === ruleValueBool
      : leadValue !== ruleValueBool
  }

  // Handle numeric fields
  if (typeof leadValue === 'number') {
    const ruleValueNum = parseNumericValue(rule.value)

    switch (rule.operator) {
      case '=':
        return leadValue === ruleValueNum
      case '!=':
        return leadValue !== ruleValueNum
      case '>':
        return leadValue > ruleValueNum
      case '>=':
        return leadValue >= ruleValueNum
      case '<':
        return leadValue < ruleValueNum
      case '<=':
        return leadValue <= ruleValueNum
      default:
        return false
    }
  }

  // Handle string fields
  if (typeof leadValue === 'string') {
    const ruleValueStr = rule.value.toLowerCase()
    const leadValueStr = leadValue.toLowerCase()

    switch (rule.operator) {
      case '=':
        return leadValueStr === ruleValueStr
      case '!=':
        return leadValueStr !== ruleValueStr
      case 'contains':
        return leadValueStr.includes(ruleValueStr)
      default:
        return false
    }
  }

  return false
}

export function evaluateAllRules(lead: PoolLead, rules: Rule[]): boolean {
  if (rules.length === 0) return true

  // Initial result based on first rule
  let result = evaluateRule(lead, rules[0])

  // Iterate through remaining rules applying logic
  for (let i = 1; i < rules.length; i++) {
    const rule = rules[i]
    const ruleResult = evaluateRule(lead, rule)

    // Logic applies to how this rule connects to the previous chain
    // Note: The data structure has logic on the rule itself.
    // Usually logic property defines relationship with PREVIOUS rule.
    // e.g. Rule 2's 'AND' means (Rule 1) AND (Rule 2)

    if (rule.logic === 'OR') {
      result = result || ruleResult
    } else {
      // Default to AND
      result = result && ruleResult
    }
  }

  return result
}

export function findQualifyingLeads(
  pool: PoolLead[],
  rules: Rule[],
): PoolLead[] {
  return pool.filter((lead) => evaluateAllRules(lead, rules))
}
